import { useState, useEffect, useCallback } from 'react';
// @mui
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import { useSnackbar } from 'src/components/snackbar';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
// routes
import { paths } from 'src/routes/paths';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
import { fDate, fDateTime } from 'src/utils/format-time';
// sections
import ReservationFormDialog from '../reservation-form-dialog';

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'Pendente', label: 'Pendente' },
  { value: 'Confirmada', label: 'Confirmada' },
  { value: 'Cancelada', label: 'Cancelada' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'Confirmada':
      return 'success';
    case 'Cancelada':
      return 'error';
    case 'Pendente':
    default:
      return 'warning';
  }
};

// ----------------------------------------------------------------------

export default function ReservationListView() {
  const settings = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (statusFilter) params.append('status', statusFilter);
      if (fromDate) params.append('fromDate', fromDate.toISOString());
      if (toDate) params.append('toDate', toDate.toISOString());

      const url = `${API_ENDPOINTS.reservations.list}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url);
      setReservations(response.data);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao carregar reservas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, statusFilter, fromDate, toDate]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API_ENDPOINTS.reservations.updateStatus}${id}/status?status=${newStatus}`);
      enqueueSnackbar(`Reserva ${newStatus.toLowerCase()}`, { variant: 'success' });
      fetchReservations();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Erro ao atualizar estado', { variant: 'error' });
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setFromDate(null);
    setToDate(null);
  };

  const handleOpenForm = () => {
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    fetchReservations();
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Reservas"
        links={[
          { name: 'Dashboard', href: paths.dashboard.root },
          { name: 'Reservas' },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleOpenForm}
          >
            Nova Reserva
          </Button>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <TextField
            select
            label="Estado"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            size="small"
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <DatePicker
            label="Data Início"
            value={fromDate}
            onChange={setFromDate}
            slotProps={{ textField: { size: 'small' } }}
          />

          <DatePicker
            label="Data Fim"
            value={toDate}
            onChange={setToDate}
            slotProps={{ textField: { size: 'small' } }}
          />

          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClearFilters}
            startIcon={<Iconify icon="mdi:filter-off" />}
          >
            Limpar
          </Button>
        </Stack>
      </Card>

      <Card>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 900 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell align="center">Pessoas</TableCell>
                  <TableCell>Notas</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.map((reservation) => (
                  <TableRow key={reservation.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{reservation.fullName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{reservation.email}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reservation.phoneNumber}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {fDateTime(reservation.date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={reservation.personsAmmount}
                        size="small"
                        variant="soft"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {reservation.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={reservation.status}
                        size="small"
                        color={getStatusColor(reservation.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                        {reservation.status === 'Pendente' && (
                          <>
                            <Tooltip title="Confirmar">
                              <IconButton
                                onClick={() => handleUpdateStatus(reservation.id, 'Confirmada')}
                                size="small"
                                color="success"
                              >
                                <Iconify icon="mdi:check-circle" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                              <IconButton
                                onClick={() => handleUpdateStatus(reservation.id, 'Cancelada')}
                                size="small"
                                color="error"
                              >
                                <Iconify icon="mdi:close-circle" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {reservation.status === 'Confirmada' && (
                          <Tooltip title="Cancelar">
                            <IconButton
                              onClick={() => handleUpdateStatus(reservation.id, 'Cancelada')}
                              size="small"
                              color="error"
                            >
                              <Iconify icon="mdi:close-circle" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {reservation.status === 'Cancelada' && (
                          <Tooltip title="Reativar">
                            <IconButton
                              onClick={() => handleUpdateStatus(reservation.id, 'Pendente')}
                              size="small"
                              color="warning"
                            >
                              <Iconify icon="mdi:refresh" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && reservations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhuma reserva encontrada
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>

      {/* Create Reservation Dialog */}
      <ReservationFormDialog
        open={formOpen}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
      />
    </Container>
  );
}
